import type {
  BarcodeLabelElement,
  CzLogoLabelElement,
  DataMatrixCodeSizeMm,
  DatamatrixLabelElement,
  DataMatrixMode,
  DecorativeShapeKind,
  EacLogoLabelElement,
  FontStyleType,
  FontWeightType,
  LabelElement,
  LabelElementType,
  LocationStripLabelElement,
  QrLabelElement,
  RotationType,
  SvgLabelElement,
  TextAlignType,
  TextDecorationType,
  TextLabelElement,
} from '@/types/label-template';
import { snapDataMatrixCodeSizeMm } from '@/utils/datamatrix-size';
import {
  buildShapeSvgMarkup,
  clampDecorativeStrokeWidth,
  DEFAULT_DECORATIVE_STROKE_WIDTH,
  type ShapeGraphicKind,
} from '@/utils/shape-svgs';

const LEGACY_KEYS = [
  'barcodeVisible',
  'barcodeFullWidth',
  'barcodeShowText',
  'barcodeFormat',
  'barcodeTextFont',
] as const;

const VALID_TYPES = new Set<LabelElementType>([
  'text',
  'barcode',
  'qr',
  'datamatrix',
  'svg',
  'czLogo',
  'eacLogo',
  'locationStrip',
]);

const DECORATIVE_KINDS = new Set<DecorativeShapeKind>([
  'rectangle',
  'line',
  'arrow',
  'ellipse',
  'star',
]);

function scrubLegacy(raw: Record<string, unknown>): Record<string, unknown> {
  const o = { ...raw };
  for (const k of LEGACY_KEYS) delete o[k];
  return o;
}

function readRotation(v: unknown): RotationType {
  const n = Number(v);
  if (n === 90 || n === 180 || n === 270) return n;
  return 0;
}

function readFontWeight(v: unknown): FontWeightType {
  return v === 'bold' ? 'bold' : 'normal';
}

function readFontStyle(v: unknown): FontStyleType {
  return v === 'italic' ? 'italic' : 'normal';
}

function readTextDecoration(v: unknown): TextDecorationType {
  return v === 'underline' ? 'underline' : 'none';
}

function readAlign(v: unknown): TextAlignType {
  if (v === 'center' || v === 'right') return v;
  return 'left';
}

function readDataMatrixMode(v: unknown): DataMatrixMode {
  return v === 'real' ? 'real' : 'test';
}

function readCore(r: Record<string, unknown>) {
  let value = typeof r.value === 'string' ? r.value : String(r.value ?? '');
  value = value
    .replace(/\{\{\s*labelNumber\s*\}\}/g, '{{number}}')
    .replace(/\{\{\s*sizeRuCombined\s*\}\}/g, '{{size}}-{{techSize}}');
  let binding = String(r.binding ?? '');
  if (binding === 'label.labelNumber') binding = 'label.number';
  const core = {
    id: String(r.id ?? ''),
    label: String(r.label ?? ''),
    binding,
    x: Number(r.x) || 0,
    y: Number(r.y) || 0,
    width: Number(r.width) || 10,
    height: Number(r.height) || 5,
    rotation: readRotation(r.rotation),
    value,
    ...(typeof r.hidden === 'boolean' ? { hidden: r.hidden } : {}),
    ...(typeof r.locked === 'boolean' ? { locked: r.locked } : {}),
  };
  return core;
}

function toText(r: Record<string, unknown>): TextLabelElement {
  const c = readCore(r);
  return {
    ...c,
    type: 'text',
    fontSize: Number(r.fontSize) || 14,
    lineHeight: Number(r.lineHeight) || 1.2,
    letterSpacing: Number(r.letterSpacing) || 0,
    fontWeight: readFontWeight(r.fontWeight),
    fontStyle: readFontStyle(r.fontStyle),
    textDecoration: readTextDecoration(r.textDecoration),
    align: readAlign(r.align),
    showLabel: typeof r.showLabel === 'boolean' ? r.showLabel : false,
    wrap: typeof r.wrap === 'boolean' ? r.wrap : true,
  };
}

function toBarcode(r: Record<string, unknown>): BarcodeLabelElement {
  const c = readCore(r);
  return {
    ...c,
    type: 'barcode',
    barcodeWidth: Number(r.barcodeWidth) || 35,
    barcodeHeight: Number(r.barcodeHeight) || 10,
    barcodePaddingTop: Number(r.barcodePaddingTop) || 0,
    barcodePaddingLeft: Number(r.barcodePaddingLeft) || 0,
    barcodePaddingBottom: Number(r.barcodePaddingBottom) || 0,
    barcodePaddingRight: Number(r.barcodePaddingRight) || 0,
    barcodeAlign: readAlign(r.barcodeAlign),
    barcodeTextFontSize: Number(r.barcodeTextFontSize) || 14,
    barcodeTextLineHeight: Number(r.barcodeTextLineHeight) || 0,
    barcodeTextLetterSpacing: Number(r.barcodeTextLetterSpacing) || 0,
  };
}

function toQr(r: Record<string, unknown>): QrLabelElement {
  return { ...readCore(r), type: 'qr' };
}

function toCzLogo(r: Record<string, unknown>): CzLogoLabelElement {
  return { ...readCore(r), type: 'czLogo' };
}

function toEacLogo(r: Record<string, unknown>): EacLogoLabelElement {
  return { ...readCore(r), type: 'eacLogo' };
}

function toDatamatrix(r: Record<string, unknown>): DatamatrixLabelElement {
  const c = readCore(r);
  const isPreset = (v: unknown): v is DataMatrixCodeSizeMm => v === 20 || v === 25 || v === 30;
  let mm: number | undefined;
  if (isPreset(r.datamatrixCodeSizeMm)) mm = r.datamatrixCodeSizeMm;
  else if (isPreset(r.width)) mm = r.width as number;
  else if (isPreset(r.height)) mm = r.height as number;
  else {
    const legacy = Number(r.datamatrixSize) || 0;
    mm = legacy >= 80 ? 30 : legacy >= 54 ? 25 : 20;
  }
  const snapped = snapDataMatrixCodeSizeMm(Number(mm));
  return {
    ...c,
    type: 'datamatrix',
    width: snapped,
    height: snapped,
    datamatrixCodeSizeMm: snapped,
    datamatrixSize: 0,
    datamatrixVisible: typeof r.datamatrixVisible === 'boolean' ? r.datamatrixVisible : true,
    datamatrixPaddingTop: Number(r.datamatrixPaddingTop) || 0,
    datamatrixPaddingLeft: Number(r.datamatrixPaddingLeft) || 0,
    datamatrixPaddingBottom: Number(r.datamatrixPaddingBottom) || 0,
    datamatrixPaddingRight: Number(r.datamatrixPaddingRight) || 0,
    datamatrixCornerMarkers: typeof r.datamatrixCornerMarkers === 'boolean' ? r.datamatrixCornerMarkers : true,
    datamatrixMode: readDataMatrixMode(r.datamatrixMode),
    datamatrixManualValue: typeof r.datamatrixManualValue === 'boolean' ? r.datamatrixManualValue : false,
  };
}

function toSvg(r: Record<string, unknown>): SvgLabelElement {
  const c = readCore(r);
  const rawKind = r.decorativeShapeKind;
  const decorativeShapeKind =
    typeof rawKind === 'string' && DECORATIVE_KINDS.has(rawKind as DecorativeShapeKind)
      ? (rawKind as DecorativeShapeKind)
      : undefined;
  let decorativeStrokeWidth =
    r.decorativeStrokeWidth != null && Number.isFinite(Number(r.decorativeStrokeWidth))
      ? Number(r.decorativeStrokeWidth)
      : undefined;
  let svgCode = typeof r.svgCode === 'string' ? r.svgCode : undefined;
  if (decorativeShapeKind) {
    const sw = clampDecorativeStrokeWidth(
      decorativeStrokeWidth ?? DEFAULT_DECORATIVE_STROKE_WIDTH,
    );
    decorativeStrokeWidth = sw;
    svgCode = buildShapeSvgMarkup(decorativeShapeKind as ShapeGraphicKind, sw);
  }
  const el: SvgLabelElement = {
    ...c,
    type: 'svg',
    catalogElementId: typeof r.catalogElementId === 'string' ? r.catalogElementId : undefined,
    svgCode,
    svgVisible: typeof r.svgVisible === 'boolean' ? r.svgVisible : true,
    svgPaddingTop: Number(r.svgPaddingTop) || 0,
    svgPaddingLeft: Number(r.svgPaddingLeft) || 0,
    svgPaddingBottom: Number(r.svgPaddingBottom) || 0,
    svgPaddingRight: Number(r.svgPaddingRight) || 0,
    svgAlign: readAlign(r.svgAlign),
  };
  if (decorativeShapeKind) {
    el.decorativeShapeKind = decorativeShapeKind;
    el.decorativeStrokeWidth = decorativeStrokeWidth;
  }
  return el;
}

function toLocationStrip(r: Record<string, unknown>): LocationStripLabelElement {
  const c = readCore(r);
  return {
    ...c,
    type: 'locationStrip',
    fontSize: Number(r.fontSize) || 11,
    lineHeight: Number(r.lineHeight) || 1.2,
    letterSpacing: Number(r.letterSpacing) || 0,
    fontWeight: readFontWeight(r.fontWeight),
    fontStyle: readFontStyle(r.fontStyle),
    textDecoration: readTextDecoration(r.textDecoration),
    align: readAlign(r.align),
    showLabel: typeof r.showLabel === 'boolean' ? r.showLabel : false,
    wrap: typeof r.wrap === 'boolean' ? r.wrap : true,
    locationStripLabelZone: String(r.locationStripLabelZone ?? 'Зона'),
    locationStripLabelRow: String(r.locationStripLabelRow ?? 'Ряд'),
    locationStripLabelCell: String(r.locationStripLabelCell ?? 'Ячейка'),
    locationStripLabelLevel: String(r.locationStripLabelLevel ?? 'Уровень'),
    locationStripCaptionFontSize: Number(r.locationStripCaptionFontSize) || 9,
  };
}

/**
 * Приводит сырой объект (JSON шаблона, spread при patch) к дискриминированному `LabelElement`
 * только с полями, нужными для `type`. Удаляет устаревшие ключи и нормализует DataMatrix / SVG-фигуры.
 */
export function toLabelElement(raw: Record<string, unknown>): LabelElement {
  const r = scrubLegacy(raw);
  const t = r.type as LabelElementType | undefined;
  if (!t || !VALID_TYPES.has(t)) return toText(r);
  switch (t) {
    case 'text':
      return toText(r);
    case 'barcode':
      return toBarcode(r);
    case 'qr':
      return toQr(r);
    case 'datamatrix':
      return toDatamatrix(r);
    case 'svg':
      return toSvg(r);
    case 'czLogo':
      return toCzLogo(r);
    case 'eacLogo':
      return toEacLogo(r);
    case 'locationStrip':
      return toLocationStrip(r);
    default:
      return toText(r);
  }
}
